import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowLeft, Plus, Trash, FloppyDisk, CheckCircle } from "@phosphor-icons/react";

const QTYPES = ["text", "textarea", "email", "tel", "select", "radio", "checkbox"];

const newId = () => Math.random().toString(36).slice(2, 10);

const AdminEventEdit = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/admin/events`).then(({ data }) => {
      const found = data.find((e) => e.id === id);
      setEvent(found || null);
    });
  }, [id]);

  const set = (key, value) => setEvent((e) => ({ ...e, [key]: value }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await api.put(`/admin/events/${id}`, event);
      setEvent(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => set("questions", [...(event.questions || []), { id: newId(), label: "New question", type: "text", required: true, options: [], placeholder: "" }]);
  const updateQuestion = (qid, patch) => set("questions", event.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)));
  const removeQuestion = (qid) => set("questions", event.questions.filter((q) => q.id !== qid));

  const addAgenda = () => set("agenda", [...(event.agenda || []), { time: "00:00", title: "Slot", speaker: "" }]);
  const updateAgenda = (i, patch) => set("agenda", event.agenda.map((a, j) => (i === j ? { ...a, ...patch } : a)));
  const removeAgenda = (i) => set("agenda", event.agenda.filter((_, j) => j !== i));

  const addSpeaker = () => set("speakers", [...(event.speakers || []), { name: "", role: "", bio: "", image: "" }]);
  const updateSpeaker = (i, patch) => set("speakers", event.speakers.map((s, j) => (i === j ? { ...s, ...patch } : s)));
  const removeSpeaker = (i) => set("speakers", event.speakers.filter((_, j) => j !== i));

  const addSponsor = () => set("sponsors", [...(event.sponsors || []), { name: "", logo: "", website: "", tier: "silver" }]);
  const updateSponsor = (i, patch) => set("sponsors", event.sponsors.map((s, j) => (i === j ? { ...s, ...patch } : s)));
  const removeSponsor = (i) => set("sponsors", event.sponsors.filter((_, j) => j !== i));

  if (!event) return <div className="font-mono-tech text-white/40">/ loading...</div>;

  return (
    <div className="cyber" data-testid="admin-event-edit-page">
      <Link to="/admin/events" className="font-mono-tech text-xs text-cyber-yellow tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> All events</Link>

      <div className="flex items-end justify-between mt-6 mb-8 flex-wrap gap-4">
        <div>
          <div className="term-label">/ edit · {event.slug}</div>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-2">{event.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="font-mono-tech text-xs text-cyber-green flex items-center gap-2"><CheckCircle size={14} weight="bold" /> Saved</span>}
          <button onClick={save} disabled={saving} className="btn-neon" data-testid="save-event-btn">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section title="/ basics">
          <Field label="Title"><input value={event.title} onChange={(e) => set("title", e.target.value)} className="w-full px-4 py-2.5" data-testid="title-input" /></Field>
          <Field label="Tagline"><input value={event.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="w-full px-4 py-2.5" /></Field>
          <Field label="Description"><textarea value={event.description || ""} onChange={(e) => set("description", e.target.value)} rows={4} className="w-full px-4 py-2.5" /></Field>
          <Field label="Hero image URL"><input value={event.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className="w-full px-4 py-2.5" /></Field>
        </Section>

        <Section title="/ logistics">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start (ISO)"><input value={event.date} onChange={(e) => set("date", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="End (ISO)"><input value={event.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Venue"><input value={event.venue || ""} onChange={(e) => set("venue", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="City"><input value={event.city || ""} onChange={(e) => set("city", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Location"><input value={event.location || ""} onChange={(e) => set("location", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Capacity"><input type="number" value={event.capacity || 0} onChange={(e) => set("capacity", parseInt(e.target.value || 0))} className="w-full px-4 py-2.5" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Status">
              <select value={event.status} onChange={(e) => set("status", e.target.value)} className="w-full px-4 py-2.5" data-testid="status-select">
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </Field>
            <Field label="Featured">
              <select value={event.featured ? "yes" : "no"} onChange={(e) => set("featured", e.target.value === "yes")} className="w-full px-4 py-2.5">
                <option value="no">no</option>
                <option value="yes">yes</option>
              </select>
            </Field>
            <Field label="Date TBD (show 'TBD' instead of date)">
              <select value={event.date_tbd ? "yes" : "no"} onChange={(e) => set("date_tbd", e.target.value === "yes")} className="w-full px-4 py-2.5" data-testid="date-tbd-select">
                <option value="no">no — show date</option>
                <option value="yes">yes — show TBD</option>
              </select>
            </Field>
            <Field label="RSVP button">
              <select value={(event.rsvp_active ?? true) ? "active" : "inactive"} onChange={(e) => set("rsvp_active", e.target.value === "active")} className="w-full px-4 py-2.5" data-testid="rsvp-active-select">
                <option value="active">active — accept RSVPs</option>
                <option value="inactive">inactive — collect notify-me signups</option>
              </select>
            </Field>
          </div>
        </Section>
      </div>

      <Section title="/ sections — show / hide on event page" className="mt-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Agenda section">
            <select value={(event.show_agenda ?? true) ? "show" : "hide"} onChange={(e) => set("show_agenda", e.target.value === "show")} className="w-full px-3 py-2" data-testid="toggle-agenda">
              <option value="show">show</option>
              <option value="hide">hide</option>
            </select>
          </Field>
          <Field label="Speakers section">
            <select value={(event.show_speakers ?? true) ? "show" : "hide"} onChange={(e) => set("show_speakers", e.target.value === "show")} className="w-full px-3 py-2" data-testid="toggle-speakers">
              <option value="show">show</option>
              <option value="hide">hide</option>
            </select>
          </Field>
          <Field label="Sponsors section">
            <select value={(event.show_sponsors ?? true) ? "show" : "hide"} onChange={(e) => set("show_sponsors", e.target.value === "show")} className="w-full px-3 py-2" data-testid="toggle-sponsors">
              <option value="show">show</option>
              <option value="hide">hide</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title="/ sponsor section copy" className="mt-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Field label="Title (e.g. 'Powered by.', 'Our partners.')">
            <input value={event.sponsors_title || ""} onChange={(e) => set("sponsors_title", e.target.value)} placeholder="Powered by." className="w-full px-4 py-2.5" data-testid="sponsors-title-input" />
          </Field>
          <Field label="Intro paragraph">
            <textarea value={event.sponsors_intro || ""} onChange={(e) => set("sponsors_intro", e.target.value)} rows={3} placeholder="Don't miss what these category leaders..." className="w-full px-4 py-2.5" data-testid="sponsors-intro-input" />
          </Field>
        </div>
      </Section>

      <Section title="/ rsvp questions" className="mt-3">
        <div className="space-y-3">
          {(event.questions || []).map((q) => (
            <div key={q.id} className="hard-border p-4 bg-cyber-elevated" data-testid={`q-row-${q.id}`}>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-5">
                  <Field label="Label"><input value={q.label} onChange={(e) => updateQuestion(q.id, { label: e.target.value })} className="w-full px-3 py-2" /></Field>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Field label="Type">
                    <select value={q.type} onChange={(e) => updateQuestion(q.id, { type: e.target.value })} className="w-full px-3 py-2">
                      {QTYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Field label="Required">
                    <select value={q.required ? "yes" : "no"} onChange={(e) => updateQuestion(q.id, { required: e.target.value === "yes" })} className="w-full px-3 py-2">
                      <option value="yes">yes</option>
                      <option value="no">no</option>
                    </select>
                  </Field>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <Field label="Placeholder / helper"><input value={q.placeholder || ""} onChange={(e) => updateQuestion(q.id, { placeholder: e.target.value })} className="w-full px-3 py-2" /></Field>
                </div>
                {(q.type === "select" || q.type === "radio") && (
                  <div className="col-span-12">
                    <Field label="Options (comma separated)">
                      <input value={(q.options || []).join(", ")} onChange={(e) => updateQuestion(q.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full px-3 py-2" />
                    </Field>
                  </div>
                )}
              </div>
              <div className="mt-3 flex justify-end">
                <button onClick={() => removeQuestion(q.id)} className="font-mono-tech text-xs text-cyber-red flex items-center gap-1 hover:text-white" data-testid={`q-remove-${q.id}`}><Trash size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addQuestion} className="btn-ghost-neon mt-4" data-testid="add-question-btn"><Plus size={12} weight="bold" /> Add question</button>
      </Section>

      <Section title="/ agenda" className="mt-3">
        <div className="space-y-2">
          {(event.agenda || []).map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 hard-border p-3 bg-cyber-elevated">
              <div className="col-span-3"><input value={a.time} onChange={(e) => updateAgenda(i, { time: e.target.value })} placeholder="HH:MM" className="w-full px-3 py-2" /></div>
              <div className="col-span-5"><input value={a.title} onChange={(e) => updateAgenda(i, { title: e.target.value })} placeholder="Title" className="w-full px-3 py-2" /></div>
              <div className="col-span-3"><input value={a.speaker || ""} onChange={(e) => updateAgenda(i, { speaker: e.target.value })} placeholder="Speaker" className="w-full px-3 py-2" /></div>
              <div className="col-span-1 flex items-center justify-end"><button onClick={() => removeAgenda(i)} className="text-cyber-red"><Trash size={14} /></button></div>
            </div>
          ))}
        </div>
        <button onClick={addAgenda} className="btn-ghost-neon mt-4"><Plus size={12} weight="bold" /> Add slot</button>
      </Section>

      <Section title="/ speakers" className="mt-3">
        <div className="space-y-2">
          {(event.speakers || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 hard-border p-3 bg-cyber-elevated">
              <div className="col-span-3"><input value={s.name} onChange={(e) => updateSpeaker(i, { name: e.target.value })} placeholder="Name" className="w-full px-3 py-2" /></div>
              <div className="col-span-3"><input value={s.role} onChange={(e) => updateSpeaker(i, { role: e.target.value })} placeholder="Role" className="w-full px-3 py-2" /></div>
              <div className="col-span-4"><input value={s.bio} onChange={(e) => updateSpeaker(i, { bio: e.target.value })} placeholder="Bio" className="w-full px-3 py-2" /></div>
              <div className="col-span-1"><input value={s.image} onChange={(e) => updateSpeaker(i, { image: e.target.value })} placeholder="img URL" className="w-full px-3 py-2" /></div>
              <div className="col-span-1 flex items-center justify-end"><button onClick={() => removeSpeaker(i)} className="text-cyber-red"><Trash size={14} /></button></div>
            </div>
          ))}
        </div>
        <button onClick={addSpeaker} className="btn-ghost-neon mt-4"><Plus size={12} weight="bold" /> Add speaker</button>
      </Section>

      <Section title="/ sponsors" className="mt-3">
        <div className="space-y-2">
          {(event.sponsors || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 hard-border p-3 bg-cyber-elevated" data-testid={`sponsor-row-${i}`}>
              <div className="col-span-3"><input value={s.name || ""} onChange={(e) => updateSponsor(i, { name: e.target.value })} placeholder="Sponsor name" className="w-full px-3 py-2" /></div>
              <div className="col-span-4"><input value={s.logo || ""} onChange={(e) => updateSponsor(i, { logo: e.target.value })} placeholder="Logo URL" className="w-full px-3 py-2" /></div>
              <div className="col-span-3"><input value={s.website || ""} onChange={(e) => updateSponsor(i, { website: e.target.value })} placeholder="Website (https://...)" className="w-full px-3 py-2" /></div>
              <div className="col-span-1">
                <select value={s.tier || "silver"} onChange={(e) => updateSponsor(i, { tier: e.target.value })} className="w-full px-2 py-2">
                  <option value="platinum">platinum</option>
                  <option value="gold">gold</option>
                  <option value="silver">silver</option>
                </select>
              </div>
              <div className="col-span-1 flex items-center justify-end"><button onClick={() => removeSponsor(i)} className="text-cyber-red" data-testid={`sponsor-remove-${i}`}><Trash size={14} /></button></div>
            </div>
          ))}
        </div>
        <button onClick={addSponsor} className="btn-ghost-neon mt-4" data-testid="add-sponsor-btn"><Plus size={12} weight="bold" /> Add sponsor</button>
      </Section>

      <WaitlistSection eventId={event.id} />
    </div>
  );
};

const WaitlistSection = ({ eventId }) => {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => api.get(`/admin/events/${eventId}/waitlist`).then(({ data }) => { setList(data); setLoaded(true); }), [eventId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    await api.delete(`/admin/waitlist/${id}`);
    load();
  };

  return (
    <Section title={`/ waitlist (notify-me) — ${list.length} entries`} className="mt-3">
      {!loaded && <div className="font-mono-tech text-xs text-white/40">loading...</div>}
      {loaded && list.length === 0 && <div className="font-mono-tech text-xs text-white/40">No one on the waitlist yet. People who land on this event while RSVP is inactive can leave their email here.</div>}
      {list.length > 0 && (
        <div className="space-y-1">
          <div className="grid grid-cols-12 gap-2 px-2 py-2 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40 border-b border-cyber-line">
            <div className="col-span-3">Name</div>
            <div className="col-span-5">Email</div>
            <div className="col-span-3">Added</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {list.map((w) => (
            <div key={w.id} className="grid grid-cols-12 gap-2 px-2 py-2 items-center text-sm border-b border-cyber-line/50" data-testid={`waitlist-row-${w.id}`}>
              <div className="col-span-3 text-white">{w.first_name} {w.last_name}</div>
              <div className="col-span-5 font-mono-tech text-xs text-white/70">{w.email}</div>
              <div className="col-span-3 font-mono-tech text-xs text-white/50">{(w.created_at || "").slice(0, 10)}</div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => remove(w.id)} className="text-cyber-red hover:text-white"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

const Section = ({ title, children, className = "" }) => (
  <section className={`hard-border bg-cyber-surface p-6 ${className}`}>
    <div className="term-label mb-4">{title}</div>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/60">{label}</label>
    {children}
  </div>
);

export default AdminEventEdit;
